import styled, { css } from 'vue-styled-components';
import { mapGetters, mapState, mapMutations } from 'vuex';
import { reactive, toRefs } from 'vue';

import WithAccountModal from '@components/hocs/WithAccountModal';
import PermissionTooltip from '@components/Common/PermissionTooltip';

import InviteMemberIcon from '@svgIcons/nb/invite-member.svg';
import AddWorkspaceIcon from '@svgIcons/nb/circle-add.svg';
import LogoutIcon from '@svgIcons/nb/logout.svg';
import NextIcon from '@svgIcons/next2.svg';
import CloseIcon from '@svgIcons/close2.svg';
import WorkspaceIcon from '@svgIcons/nb/workspace.svg';

import {
    FLEX_VERTICAL_CENTER_DIV,
    FLEX_COLUMN_DIV,
    FLEX_CENTER_DIV,
    FLEX_COLUMN_ALIGN_CENTER_DIV,
} from '@styles/common.styled.component';
import { BreakPoint, pickBreakpoint } from '@/utils/layout';
import { IFRAME_EVENT_MESSAGE } from '@consts';

const Workspace = {
    name: 'Workspace',
    props: {},
    setup() {
        const state = reactive({
            expanded: false,
        });

        const toggleExpanded = () => (state.expanded = !state.expanded);

        return {
            ...toRefs(state),
            toggleExpanded,
        };
    },
    computed: {
        ...mapState('user', ['accountId']),
        ...mapGetters('user', [
            'user',
            'accountId',
            'accounts',
            'defaultAccountId',
            'account',
            'teamsAndRoles',
            'isAdmin',
        ]),
        ...mapGetters('permission', ['memberInvitable']),
        color: R.pathOr('', ['account', 'color']),
        wsName: R.pathOr('', ['account', 'name']),
        email: R.pathOr('', ['globalUser', 'email']),
        routeMeta: R.pathOr({}, ['$route', 'meta']),
    },
    methods: {
        ...mapMutations('view', ['setLogoutModalActive']),
        // Modals live in the React app. Open it in the current iframe, or go
        // to Settings and let ReactSettings open it once React has loaded.
        openReactModal(type, pendingAction) {
            this.toggleExpanded();

            const iframeRefs = this.$store.getters['iframe/iframeRefs'];
            if (iframeRefs && iframeRefs.length) {
                this.postIframeMessage({ type, origin: 'VUE' });
            } else {
                this.$store.dispatch(pendingAction, true);
                this.pushToRoute({ name: 'Settings' });
            }
        },
        renderExpanded() {
            const { wsName, email, accounts, renderAccountRow } = this;

            return (
                <DropdownWrapper>
                    <MobileHeader class="lnb-workspace-panel-mobile-header">
                        <NextIcon
                            v-view-box
                            class="lnb-workspace-panel-back-icon"
                            onClick={() => this.toggleExpanded()}
                        />
                        <HeaderTitle class="adriel-ellipsis">
                            {wsName}
                        </HeaderTitle>

                        <CloseIcon
                            v-view-box
                            class="lnb-workspace-panel-close-icon"
                            onClick={() => this.toggleExpanded()}
                        />
                    </MobileHeader>
                    <WorkspaceInfoWrapper>
                        <Title class="adriel-ellipsis">{wsName}</Title>
                        <Email class="adriel-ellipsis">{email}</Email>
                        {(!this.isPlanA || this.isAdmin) && (
                            <ButtonWrapper>
                                {/* Plan A workspaces take no new members (TASK-14124). */}
                                {!this.isPlanA && (
                                    <PermissionTooltip
                                        visible={!this.memberInvitable}
                                        block
                                    >
                                        <Button
                                            disabled={!this.memberInvitable}
                                            class="lnb-workspace-panel-button"
                                            onClick={() =>
                                                this.openReactModal(
                                                    IFRAME_EVENT_MESSAGE.OPEN_INVITE_MEMBER_MODAL,
                                                    'iframe/setPendingInviteMemberModal'
                                                )
                                            }
                                        >
                                            <InviteMemberIcon
                                                v-view-box
                                                width={16}
                                                height={16}
                                            />
                                            <ButtonText>
                                                {this.$t(
                                                    'MULTI_ACCOUNT.button_invite'
                                                )}
                                            </ButtonText>
                                        </Button>
                                    </PermissionTooltip>
                                )}
                                {/* Only backoffice admins may add workspaces. */}
                                {this.isAdmin && (
                                    <Button
                                        class="lnb-workspace-panel-button"
                                        onClick={() =>
                                            this.openReactModal(
                                                IFRAME_EVENT_MESSAGE.OPEN_CREATE_WORKSPACE_MODAL,
                                                'iframe/setPendingCreateWorkspaceModal'
                                            )
                                        }
                                    >
                                        <AddWorkspaceIcon
                                            v-view-box
                                            width={16}
                                            height={16}
                                        />
                                        <ButtonText class="ja-font-size-12">
                                            {this.$t('MULTI_ACCOUNT.create_ws')}
                                        </ButtonText>
                                    </Button>
                                )}
                            </ButtonWrapper>
                        )}
                    </WorkspaceInfoWrapper>

                    <WorkspaceListWrapper class="lnb-workspace-panel-list-wrapper">
                        <WorkspaceTitle>
                            {this.$t('MULTI_ACCOUNT.workspace')}
                        </WorkspaceTitle>
                        <WorkspaceList class="lnb-workspace-panel-list">
                            {accounts.map(renderAccountRow)}
                        </WorkspaceList>
                    </WorkspaceListWrapper>
                    <LogoutButton
                        class="lnb-workspace-panel-logout-button"
                        onClick={() => {
                            this.toggleExpanded();
                            this.setLogoutModalActive(true);
                        }}
                    >
                        <LogoutIcon v-view-box width={16} height={16} />
                        <ButtonText>{this.$t('COMMON.sign_out')}</ButtonText>
                    </LogoutButton>
                </DropdownWrapper>
            );
        },
        renderAccountRow({ name = '', color, id }) {
            const selected = this.accountId == id;

            return (
                <WorkspaceItem
                    key={`${name}::${id}`}
                    onClick={() => {
                        this.changeAccount(id, this.routeMeta.canChangeAccount);
                        this.toggleExpanded();
                    }}
                >
                    <WorkspaceIcon width={20} height={20} color={color} />
                    <WorkspaceName class="adriel-ellipsis" active={selected}>
                        {name}
                    </WorkspaceName>
                </WorkspaceItem>
            );
        },
    },
    render() {
        const {
            isMobileSize,
            wsName,
            color,
            expanded,
            toggleExpanded,
            renderExpanded,
        } = this;

        return (
            <Wrapper>
                {expanded && <Backdrop onClick={toggleExpanded} />}
                {this.wrapTooltip({
                    className: 'black-tooltip',
                    placement: 'right',
                    visible: !isMobileSize,
                    title: wsName,
                    content: (
                        <Menu
                            class="workspace-button"
                            active={expanded}
                            onClick={toggleExpanded}
                        >
                            <WorkspaceIcon
                                width={20}
                                height={20}
                                color={color}
                            />
                            <TextWrapper class="lnb-menu-text-wrapper">
                                <Text active={expanded}>{wsName}</Text>
                            </TextWrapper>
                            <NextIcon v-view-box class="lnb-menu-next-icon" />
                        </Menu>
                    ),
                })}
                {expanded && renderExpanded()}
            </Wrapper>
        );
    },
};

const buttonCss = css`
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: var(--color-gray-50);
        background-color: var(--color-gray-10);
    }
    &:active {
        background-color: var(--color-gray-20);
    }
`;

/* [TODO] mobile reponsive */
const Wrapper = styled(FLEX_COLUMN_ALIGN_CENTER_DIV)`
    position: relative;
`;
const Backdrop = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
`;

const Menu = styled(FLEX_COLUMN_ALIGN_CENTER_DIV)`
    width: 60px;
    height: 60px;
    padding: 4px 0px;
    justify-content: center;
    gap: 6px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    background-color: var(--color-gray-white);

    svg {
        flex-shrink: 0;
    }

    .lnb-menu-next-icon {
        width: 24px;
        height: 24px;
        margin-left: auto;
        display: none;
    }

    &:hover {
        background-color: var(--color-gray-5);
    }

    @media screen and (max-width: ${pickBreakpoint(BreakPoint.lg) - 1}px) {
        width: 36px;
        height: 36px;

        .lnb-menu-text-wrapper {
            display: none;
        }
    }

    @media screen and (max-width: ${pickBreakpoint(BreakPoint.md) - 1}px) {
        width: 100%;
        height: 48px;
        flex-direction: row;
        justify-content: flex-start;

        .lnb-menu-text-wrapper {
            display: flex;
            flex-direction: row;

            & div {
                font-size: 15px;
                line-height: 24px;
                text-align: start;
            }
        }
        .lnb-menu-next-icon {
            display: block;
        }
    }
`;

const TextWrapper = styled(FLEX_COLUMN_ALIGN_CENTER_DIV)`
    gap: 3px;
`;

const Text = styled('div', { active: Boolean })`
    font-size: 10px;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: -0.1px;
    text-align: center;
    word-break: break-word;
    transition: all 0.2s ease;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    ${({ active }) =>
        active
            ? css`
                  color: var(--color-blue-60);
              `
            : css`
                  color: var(--color-gray-100);
              `}
`;

const DropdownWrapper = styled(FLEX_COLUMN_DIV)`
    position: fixed;

    width: 320px;
    padding: 8px 0 16px;
    gap: 4px;
    border-radius: 12px;
    border: 1px solid var(--color-gray-30);
    background: linear-gradient(180deg, var(--color-blue-10) 0%, #fff 25%),
        var(--color-gray-white);
    box-shadow: 0 6px 24px 0 rgba(60, 70, 79, 0.12),
        0 12px 48px 0 rgba(60, 70, 79, 0.08);

    @media screen and (max-width: ${pickBreakpoint(BreakPoint.md) - 1}px) {
        width: 100%;
        height: 100%;
        padding: 0 0 20px;
        top: 0;
        left: 0;
        border-radius: 0;
        background: var(--color-gray-white);

        .lnb-workspace-panel-list-wrapper {
            flex: 1;
            overflow: hidden;
        }

        .lnb-workspace-panel-list {
            flex: 1;
            height: unset;
            max-height: unset;
        }

        .lnb-workspace-panel-mobile-header {
            display: flex;

            .lnb-workspace-panel-back-icon {
                width: 24px;
                height: 24px;
                margin-right: auto;
                flex-shrink: 0;
                rotate: 180deg;
                cursor: pointer;
            }

            .lnb-workspace-panel-close-icon {
                width: 24px;
                height: 24px;
                margin-left: auto;
                flex-shrink: 0;
                cursor: pointer;
            }
        }

        .lnb-workspace-panel-button,
        .lnb-workspace-panel-logout-button {
            height: 48px;

            svg {
                width: 22px;
                height: 22px;
            }

            div {
                font-size: 16px;
                font-weight: 500;
            }
        }
    }

    @media screen and (min-width: ${pickBreakpoint(BreakPoint.md)}px) {
        left: 52px;
        bottom: 18px;
    }

    @media screen and (min-width: ${pickBreakpoint(BreakPoint.lg)}px) {
        left: 76px;
    }
`;

const MobileHeader = styled(FLEX_CENTER_DIV)`
    height: 64px;
    padding: 0 16px;
    gap: 10px;
    display: none;
`;

const HeaderTitle = styled.div`
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: -0.1px;
    color: var(--color-gray-100);
`;
const WorkspaceInfoWrapper = styled(FLEX_COLUMN_DIV)`
    padding: 12px 16px;
    gap: 4px;
`;
const Title = styled.div`
    color: var(--color-gray-120);
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: -0.1px;
`;
const Email = styled.div`
    color: var(--color-gray-80);
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.1px;
`;
const ButtonWrapper = styled(FLEX_VERTICAL_CENTER_DIV)`
    margin-top: 8px;
    gap: 4px;
    flex: 1;
`;
const Button = styled(FLEX_CENTER_DIV, { disabled: Boolean })`
    height: 32px;
    padding: 6px;
    flex: 1;
    gap: 4px;
    border-radius: 8px;
    border: 1px solid var(--color-gray-40);
    background: var(--color-gray-white);
    ${buttonCss}

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: not-allowed;
            border-color: var(--color-gray-30);

            svg {
                opacity: 0.4;
            }

            div {
                color: var(--color-gray-40);
            }
        `}
`;
const ButtonText = styled.div`
    color: var(--color-gray-100);
    font-size: 13px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.1px;
`;

const WorkspaceListWrapper = styled(FLEX_COLUMN_DIV)`
    padding: 4px 0;
    border-top: 1px solid var(--color-gray-30);
    border-bottom: 1px solid var(--color-gray-30);
`;
const WorkspaceTitle = styled(Title)`
    height: 36px;
    padding: 8px 16px;
    gap: 4px;
`;
const scrollCss = css`
    ::-webkit-scrollbar {
        -webkit-appearance: none !important;
        width: 6px !important;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: 6px !important;
        background-color: var(--color-gray-60) !important;
    }

    ::-webkit-scrollbar-thumb:hover {
        background-color: var(--color-gray-70) !important;
    }
`;
const WorkspaceList = styled(FLEX_COLUMN_DIV)`
    height: 252px;
    max-height: 252px;
    overflow-y: auto;
    ${scrollCss}
`;
const WorkspaceItem = styled(FLEX_VERTICAL_CENTER_DIV)`
    height: 36px;
    padding: 8px;
    margin: 0 8px;
    gap: 4px;
    border-radius: 4px;
    ${buttonCss}

    svg {
        flex-shrink: 0;
    }
`;

const WorkspaceName = styled('div', { active: Boolean })`
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.1px;

    ${({ active }) =>
        active
            ? css`
                  color: var(--color-blue-60);
              `
            : css`
                  color: var(--color-gray-100);
              `}
`;
const LogoutButton = styled(FLEX_VERTICAL_CENTER_DIV)`
    height: 32px;
    margin: 8px 8px 0;
    padding: 0 8px;
    gap: 4px;
    background: var(--color-gray-white);
    border-radius: 4px;
    ${buttonCss}
`;

export default WithAccountModal(Workspace);
